// ============================================================
// TATHYA DMS - User Master
// Version: 1.1.0
// Date: 2026-04-21
// Change (v1.1): CEO Clause 4 — Status filter (Active/Deactivated/All)
//         and Role filter (All/Super Admin/User) above the user table.
// ============================================================

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  getUsers, createUser, deactivateUser,
  reactivateUser, resetUserPassword
} from '../../services/api';

const UserMaster = () => {
  const { org_slug } = useParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [resetModal, setResetModal] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [resetPwd, setResetPwd] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');   // active | inactive | all
  const [roleFilter, setRoleFilter] = useState('all');          // all | super_admin | user
  const [searchTerm, setSearchTerm] = useState('');

  const loadUsers = async () => {
    try {
      const res = await getUsers(org_slug);
      setUsers(res.data);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, [org_slug]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    setSubmitting(true);
    try {
      await createUser(org_slug, form);
      setSuccess(`User ${form.name} created successfully`);
      setForm({ name: '', email: '', password: '' });
      setShowForm(false);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (id, name) => {
    if (!window.confirm(`Deactivate ${name}?`)) return;
    try {
      await deactivateUser(org_slug, id);
      setSuccess(`${name} deactivated`);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    }
  };

  const handleReactivate = async (id, name) => {
    try {
      await reactivateUser(org_slug, id);
      setSuccess(`${name} reactivated`);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPwd || resetPwd.length < 8) {
      return setError('Password must be at least 8 characters');
    }
    try {
      await resetUserPassword(org_slug, resetModal.id, { password: resetPwd });
      setSuccess(`Password reset for ${resetModal.name}`);
      setResetModal(null);
      setResetPwd('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed');
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>User Master</h1>
          <p style={styles.subtitle}>
            Manage who has access to CONSAM DMS
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => { setShowForm(!showForm); setError(''); }}
        >
          {showForm ? 'Cancel' : '+ New User'}
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div style={styles.errorBox}>
          {error}
          <button onClick={() => setError('')} style={styles.closeBtn}>✕</button>
        </div>
      )}
      {success && (
        <div style={styles.successBox}>
          {success}
          <button onClick={() => setSuccess('')} style={styles.closeBtn}>✕</button>
        </div>
      )}

      {/* Create User Form */}
      {showForm && (
        <div className="card" style={styles.formCard}>
          <h3 style={styles.formTitle}>Create New User</h3>
          <form onSubmit={handleCreate}>
            <div style={styles.formGrid}>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="Enter full name"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})}
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div className="form-group">
                <label>Temporary Password *</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  placeholder="Minimum 8 characters"
                  required
                />
                <p style={styles.hint}>
                  User will be forced to change this on first login
                </p>
              </div>
            </div>
            <div style={styles.formActions}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowForm(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users Table */}
      <div className="card" style={{ marginTop: '16px' }}>

        {/* Filter bar */}
        <div style={styles.filterBar}>
          <div style={styles.filterGroup}>
            <span style={styles.filterLabel}>Status:</span>
            {[['active','Active'],['inactive','Deactivated'],['all','All']].map(([val, label]) => (
              <button
                key={val}
                className={`btn ${statusFilter === val ? 'btn-primary' : 'btn-secondary'}`}
                style={styles.filterBtn}
                onClick={() => setStatusFilter(val)}
              >
                {label}
                {' '}({users.filter(u =>
                  val === 'all' ? true : val === 'active' ? u.is_active : !u.is_active
                ).length})
              </button>
            ))}
          </div>
          <div style={styles.filterGroup}>
            <span style={styles.filterLabel}>Role:</span>
            {[['all','All'],['super_admin','Super Admin'],['user','User']].map(([val, label]) => (
              <button
                key={val}
                className={`btn ${roleFilter === val ? 'btn-primary' : 'btn-secondary'}`}
                style={styles.filterBtn}
                onClick={() => setRoleFilter(val)}
              >
                {label}
                {' '}({users.filter(u => val === 'all' ? true : u.role === val).length})
              </button>
            ))}
          </div>
          <div style={styles.filterGroup}>
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              style={styles.searchInput}
            />
          </div>
        </div>

        {loading ? (
          <p style={{ color: '#595959' }}>Loading users...</p>
        ) : users.length === 0 ? (
          <div style={styles.empty}>
            <p>No users yet. Create the first user above.</p>
          </div>
        ) : (() => {
          const filtered = users.filter(u => {
            const statusOk = statusFilter === 'all' ? true
              : statusFilter === 'active' ? u.is_active : !u.is_active;
            const roleOk = roleFilter === 'all' ? true : u.role === roleFilter;
            const terms = searchTerm.toLowerCase().trim().split(/\s+/).filter(Boolean);
            const haystack = (u.name + ' ' + u.email).toLowerCase();
            const searchOk = terms.length === 0 || terms.every(t => haystack.includes(t));
            return statusOk && roleOk && searchOk;
          });
          return filtered.length === 0 ? (
            <div style={styles.empty}>
              <p>No users match the selected filters.</p>
            </div>
          ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Role</th>
                <th style={styles.th}>Last Login</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, idx) => (
                <tr key={u.id} style={styles.tr}>
                  <td style={styles.td}>{idx + 1}</td>
                  <td style={styles.td}>
                    <div style={styles.userName}>{u.name}</div>
                    {u.password_reset_required && (
                      <span style={styles.resetBadge}>
                        password not set
                      </span>
                    )}
                  </td>
                  <td style={styles.td}>{u.email}</td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.roleBadge,
                      background: u.role === 'super_admin'
                        ? '#EDE0F5' : '#D6E4F0',
                      color: u.role === 'super_admin'
                        ? '#5B2C8D' : '#1B3A5C'
                    }}>
                      {u.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {u.last_login
                      ? new Date(u.last_login).toLocaleString()
                      : <span style={{ color: '#aaa' }}>Never</span>
                    }
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.statusBadge,
                      background: u.is_active ? '#E2F0D9' : '#FCE4D6',
                      color: u.is_active ? '#1E7B34' : '#C0392B'
                    }}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actionBtns}>
                      <button
                        className="btn btn-secondary"
                        style={styles.smallBtn}
                        onClick={() => {
                          setResetModal(u);
                          setResetPwd('');
                          setError('');
                        }}
                      >
                        Reset pwd
                      </button>
                      {u.is_active ? (
                        <button
                          className="btn btn-danger"
                          style={styles.smallBtn}
                          onClick={() => handleDeactivate(u.id, u.name)}
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          className="btn btn-primary"
                          style={styles.smallBtn}
                          onClick={() => handleReactivate(u.id, u.name)}
                        >
                          Reactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          );
        })()}
      </div>

      {/* Reset Password Modal */}
      {resetModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>
              Reset Password — {resetModal.name}
            </h3>
            <p style={styles.modalSubtitle}>
              User will be forced to change this on next login.
            </p>
            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <label>New Temporary Password</label>
                <input
                  type="password"
                  value={resetPwd}
                  onChange={e => setResetPwd(e.target.value)}
                  placeholder="Minimum 8 characters"
                  required
                  autoFocus
                />
              </div>
              {error && <p className="error-msg">{error}</p>}
              <div style={styles.modalActions}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setResetModal(null); setError(''); }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1B3A5C'
  },
  subtitle: {
    color: '#595959',
    fontSize: '14px',
    marginTop: '4px'
  },
  filterBar: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    padding: '12px 0 16px',
    borderBottom: '1px solid #e0e0e0',
    marginBottom: '16px'
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap'
  },
  filterLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#595959',
    marginRight: '4px'
  },
  filterBtn: {
    padding: '4px 10px',
    fontSize: '12px'
  },
  errorBox: {
    background: '#FCE4D6',
    color: '#C0392B',
    padding: '10px 16px',
    borderRadius: '6px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  successBox: {
    background: '#E2F0D9',
    color: '#1E7B34',
    padding: '10px 16px',
    borderRadius: '6px',
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    opacity: 0.7
  },
  formCard: {
    marginBottom: '16px'
  },
  formTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1B3A5C',
    marginBottom: '16px'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px'
  },
  hint: {
    fontSize: '12px',
    color: '#595959',
    marginTop: '4px'
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #f0f0f0'
  },
  empty: {
    textAlign: 'center',
    padding: '40px',
    color: '#595959'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  th: {
    padding: '10px 12px',
    background: '#1B3A5C',
    color: 'white',
    fontWeight: '600',
    textAlign: 'left',
    fontSize: '13px'
  },
  tr: {
    borderBottom: '1px solid #f0f0f0'
  },
  td: {
    padding: '10px 12px',
    fontSize: '13px',
    color: '#1B3A5C'
  },
  userName: {
    fontWeight: '500'
  },
  resetBadge: {
    fontSize: '10px',
    background: '#FFF2CC',
    color: '#B7600A',
    padding: '2px 6px',
    borderRadius: '4px',
    display: 'inline-block',
    marginTop: '2px'
  },
  roleBadge: {
    padding: '3px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'capitalize'
  },
  statusBadge: {
    padding: '3px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600'
  },
  actionBtns: {
    display: 'flex',
    gap: '6px'
  },
  smallBtn: {
    padding: '4px 10px',
    fontSize: '12px'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modal: {
    background: 'white',
    borderRadius: '12px',
    padding: '32px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1B3A5C',
    marginBottom: '8px'
  },
  modalSubtitle: {
    fontSize: '13px',
    color: '#595959',
    marginBottom: '20px'
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '20px'
  }
};

export default UserMaster;
